import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {UPDATE_FLOWCHART_MODAL_TYPE} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {
  ActionMethod,
  ActionOrigin,
  CloseType,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {GraphBuilderErrorType} from 'processes/graph-builder/validation/GraphBuilderConstants';
import ProcessFlowchartHorizontalEditor from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartHorizontalEditor';
import {ProcessInteractiveFlowchart} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import getEmptyFlowchart from 'processes/graph-builder-processes/util/getEmptyFlowchart';
import {ProcessFlowchartDesignSpace} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

export interface HorizontalTemplateFlowchartEditorModalProps {
  flowchart: ProcessInteractiveFlowchart | null;
  designSpace: ProcessFlowchartDesignSpace;
  onRequestSubmit: (flowchart: ProcessInteractiveFlowchart) => void;
  onRequestClose: () => void;
  eventGroupId: string;
  didInitialFlowchartHaveChainedRouters: boolean;
}

export const SAVE_WARNING_MESSAGE =
  "You can save this template flowchart, but the flowchart configuration can't be submitted until the errors are fixed.";

export const TEMPLATE_FLOWCHART_EDITOR_MODAL_TYPE = 'TEMPLATE_FLOWCHART_EDITOR_MODAL';

export default function HorizontalTemplateFlowchartEditorModal(
  props: HorizontalTemplateFlowchartEditorModalProps
): JSX.Element {
  const initialFlowchart = props.flowchart ?? getEmptyFlowchart(props.designSpace.flowchartSchema);
  const [flowchart, setFlowchart] = useState(initialFlowchart);
  const [flowchartErrors, setFlowchartErrors] = useState<Array<GraphBuilderErrorType>>([]);

  return (
    <Modal
      title="Configure template flowchart"
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: () => {
            analytics.trackEventWithModule(
              COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
              ANALYTICS_MODULE.WORKFLOWS,
              {
                event_group_id: props.eventGroupId,
                action_origin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE_FLOWCHART_CONFIGURATION,
                action_method: ActionMethod.TEMPLATE_FLOWCHART_MODIFICATION,
                task_schema_api_id: props.designSpace.flowchartSchema.id,
                close_type: CloseType.CANCEL,
                error_types: flowchartErrors,
                modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
              }
            );
            props.onRequestClose();
          },
        },
        {
          text: 'Save',
          submit: true,
          style: ButtonStyle.PRIMARY,
        },
      ]}
      bannerProps={
        flowchartErrors.length === 0
          ? null
          : {
              bannerType: InlineBannerType.WARNING,
              text: SAVE_WARNING_MESSAGE,
            }
      }
      onRequestSubmit={() => {
        analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
          event_group_id: props.eventGroupId,
          action_origin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE_FLOWCHART_CONFIGURATION,
          action_method: ActionMethod.TEMPLATE_FLOWCHART_MODIFICATION,
          task_schema_api_id: props.designSpace.flowchartSchema.id,
          error_types: flowchartErrors,
          modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
        });
        props.onRequestSubmit(flowchart);
      }}
      onRequestClose={() => {
        analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
          event_group_id: props.eventGroupId,
          action_origin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE_FLOWCHART_CONFIGURATION,
          action_method: ActionMethod.TEMPLATE_FLOWCHART_MODIFICATION,
          task_schema_api_id: props.designSpace.flowchartSchema.id,
          close_type: CloseType.EXIT,
          error_types: flowchartErrors,
          modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
        });
        props.onRequestClose();
      }}
      closeableOnEscapeKey={false}
      extraLarge={true}
      fullHeight={true}
      scrollable={false}
      bodyPadded={false}
    >
      <ProcessFlowchartHorizontalEditor
        flowchart={initialFlowchart}
        designSpace={props.designSpace}
        onFlowchartChange={(newFlowchart, flowchartErrors) => {
          setFlowchart(newFlowchart);
          setFlowchartErrors(flowchartErrors);
        }}
        editorEventGroupId={props.eventGroupId}
        actionMethod={ActionMethod.TEMPLATE_FLOWCHART_MODIFICATION}
        modalType={TEMPLATE_FLOWCHART_EDITOR_MODAL_TYPE}
        didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
      />
    </Modal>
  );
}
